import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';
import type { TabViewProps } from '@verclam/ui-kit';
import { TabView } from '@verclam/ui-kit';

/**
 * Dresses the kit's tabs as the navigation chips used everywhere else: pill, `chip` type, and the
 * `appBar.navChip` pair. The kit's own frame is dropped — nav background, bottom rule, ink bar and
 * panel padding — because every caller already carries its own cards on the page canvas.
 *
 * Written outside `@layer ui-kit`, so it wins over the kit's rest/hover/active rules whatever their
 * specificity. That is also why hover and active are restated here: overriding the rest state alone
 * would leave the kit's variants unable to repaint it. `:not([data-active])` keeps hover from
 * outweighing the selected tab, which it outranks by one pseudo-class.
 */
const pillTabsStyle = css`
	.ui-kit-tab-view-nav {
		gap: 0.75rem;
		background-color: transparent;
		padding-bottom: 0.5rem;
		border-bottom: 1px solid ${theme.colors.border};
	}

	.ui-kit-tab-view-tab {
		${theme.typography.chip}
		gap: 0.5rem;
		padding: 0.75rem 1.25rem;
		font-size: ${theme.fontSizes.medium};
		border-bottom: none;
		border-radius: ${theme.radii.pill};
		background-color: ${theme.appBar.navChip.background};
		color: ${theme.appBar.navChip.text};
	}

	.ui-kit-tab-view-tab:not([data-active]):hover {
		background-color: ${theme.colors.surface};
		color: ${theme.colors.ink};
	}

	.ui-kit-tab-view-tab[data-active] {
		background-color: ${theme.appBar.navChipActive.background};
		color: ${theme.appBar.navChipActive.text};
	}

	.ui-kit-tab-view-ink-bar {
		display: none;
	}

	.ui-kit-tab-view-panels {
		background-color: transparent;
	}

	.ui-kit-tab-view-panel {
		padding: 1.25rem 0 0;
	}
	.ui-kit-tab-view-next-button, .ui-kit-tab-view-prev-button {
		margin-bottom: .5rem;
	}
`;

/**
 * Pins the nav bar to the top of the nearest scrolling ancestor. Relies on the caller's own scroll
 * container (`overflow-y: auto`, no `overflow: hidden` in between) to make the sticky effective —
 * this component never poses an overflow itself. `.ui-kit-tab-view-nav-content` already carries
 * `overflow-x: auto` for horizontal scrolling on mobile; it is its parent, `navContainer`, that
 * sticks.
 */
const stickyStyle = css`
	.ui-kit-tab-view-nav-container {
		position: sticky;
		top: 0;
		z-index: 1;
		background-color: ${theme.colors.canvas};
	}
`;

type Props = Readonly<
	Pick<TabViewProps, 'activeIndex' | 'onTabChange' | 'onBeforeTabChange' | 'scrollable' | 'children'>
> & {
	/** Sticks `.ui-kit-tab-view-nav-container` to the top of the nearest scrolling ancestor. */
	readonly isSticky?: boolean;
};

/**
 * Dresses `TabView` as the pill-shaped navigation chips used everywhere in the project, the only
 * tab styling in the codebase. Relayed props keep the kit's own signatures untouched.
 */
const PillTabView: React.FC<Props> = ({
	activeIndex,
	onTabChange,
	onBeforeTabChange,
	scrollable,
	isSticky = false,
	children,
}) => {
	return (
		<TabView
			css={isSticky ? [pillTabsStyle, stickyStyle] : pillTabsStyle}
			activeIndex={activeIndex}
			onTabChange={onTabChange}
			onBeforeTabChange={onBeforeTabChange}
			scrollable={scrollable}
		>
			{children}
		</TabView>
	);
};

export default PillTabView;
